import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { Icon, Waveform, StatusBadge, Spinner } from '../components/ui';

interface AudioFile {
  id: string;
  name: string;
  public_url: string;
  file_size: number;
  format: string;
  duration_seconds: number;
  text_preview: string;
  character_count: number;
  provider: string;
  voice_id: string;
  voice_name: string | null;
  created_at: string;
}

const GRID_COLS = 'grid grid-cols-[52px_1.6fr_1fr_72px_72px_200px] gap-[14px] items-center';

const PAGE_SIZE = 50;

export function AudioLibrary() {
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState('');
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  useEffect(() => {
    loadAudioFiles();
  }, []);

  const loadAudioFiles = async () => {
    setLoading(true);
    const result = await api.getAudioFiles({ limit: PAGE_SIZE });
    if (result.success && result.data) {
      setAudioFiles(result.data);
      // A full page means there may be another one behind it.
      setHasMore(result.data.length === (result.pagination?.limit ?? PAGE_SIZE));
    }
    setLoading(false);
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const result = await api.getAudioFiles({ limit: PAGE_SIZE, offset: audioFiles.length });
    if (result.success && result.data) {
      const batch = result.data;
      setAudioFiles(prev => {
        const known = new Set(prev.map(f => f.id));
        return [...prev, ...batch.filter(f => !known.has(f.id))];
      });
      setHasMore(batch.length === (result.pagination?.limit ?? PAGE_SIZE));
    }
    setLoadingMore(false);
  };

  const handleDelete = async (fileId: string) => {
    const result = await api.deleteAudioFile(fileId);
    if (result.success) {
      setAudioFiles(audioFiles.filter(f => f.id !== fileId));
    }
    setDeleteConfirm(null);
  };

  const togglePlay = (fileId: string) => {
    const audio = audioRefs.current[fileId];
    if (!audio) return;
    if (playingId === fileId) {
      audio.pause();
    } else {
      if (playingId) {
        audioRefs.current[playingId]?.pause();
      }
      audio.play();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const fileTitle = (file: AudioFile) => file.name || file.text_preview || 'Untitled audio';

  const startRename = (file: AudioFile) => {
    setRenameError('');
    setRenamingId(file.id);
    setRenameValue(file.name || '');
  };

  const saveRename = async () => {
    if (!renamingId || renameSaving) return;
    const name = renameValue.trim();
    if (!name) {
      setRenameError('Name cannot be empty');
      return;
    }
    setRenameSaving(true);
    const result = await api.renameAudioFile(renamingId, name);
    setRenameSaving(false);
    if (result.success) {
      setAudioFiles(prev => prev.map(f => (f.id === renamingId ? { ...f, name } : f)));
      setRenamingId(null);
      setRenameError('');
    } else {
      setRenameError(result.error?.message || 'Failed to rename');
    }
  };

  const renderTitle = (file: AudioFile, textClass: string) =>
    renamingId === file.id ? (
      <span className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
        <input
          type="text"
          value={renameValue}
          maxLength={120}
          autoFocus
          onChange={e => setRenameValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') saveRename();
            if (e.key === 'Escape') setRenamingId(null);
          }}
          className="input flex-1 min-w-0 !py-[5px] !text-[13px]"
        />
        <button
          type="button"
          onClick={saveRename}
          disabled={renameSaving}
          aria-label="Save name"
          className="flex-none text-[var(--a-core)] hover:opacity-80 disabled:opacity-40"
        >
          <Icon name="check" size={16} />
        </button>
        <button
          type="button"
          onClick={() => setRenamingId(null)}
          aria-label="Cancel rename"
          className="flex-none text-mist-500 hover:text-mist-300 text-[12px] font-semibold"
        >
          Cancel
        </button>
        {renameError && <span className="text-[11.5px] text-[#F0928C] whitespace-nowrap">{renameError}</span>}
      </span>
    ) : (
      <p className={`m-0 text-white ${textClass} font-semibold truncate`}>{fileTitle(file)}</p>
    );

  const renderRenameButton = (file: AudioFile) => (
    <button
      type="button"
      onClick={() => startRename(file)}
      aria-label="Rename audio file"
      className="flex-none text-mist-500 transition-colors hover:text-white"
    >
      <Icon name="pencil" size={15} />
    </button>
  );

  const filtered = audioFiles.filter(f => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      fileTitle(f).toLowerCase().includes(q) ||
      (f.voice_name || f.voice_id).toLowerCase().includes(q) ||
      (f.text_preview || '').toLowerCase().includes(q)
    );
  });

  const totalBytes = audioFiles.reduce((sum, f) => sum + (f.file_size || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={34} />
      </div>
    );
  }

  const renderPlayButton = (file: AudioFile, sizeClass: string) => (
    <button
      type="button"
      onClick={() => togglePlay(file.id)}
      aria-label={playingId === file.id ? 'Pause' : 'Play'}
      className={`${sizeClass} flex-none flex items-center justify-center rounded-[9px] border border-ink-600 bg-ink-950 cursor-pointer transition-colors hover:border-ink-500`}
      style={{ color: 'var(--a-core)' }}
    >
      <Icon name={playingId === file.id ? 'pause' : 'play'} size={15} fill="var(--a-core)" />
    </button>
  );

  const renderDelete = (file: AudioFile) =>
    deleteConfirm === file.id ? (
      <span className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleDelete(file.id)}
          className="text-[12.5px] font-semibold text-[#F0928C] hover:opacity-80"
        >
          Confirm
        </button>
        <button
          type="button"
          onClick={() => setDeleteConfirm(null)}
          className="text-[12.5px] text-mist-500 hover:text-mist-300"
        >
          Cancel
        </button>
      </span>
    ) : (
      <button
        type="button"
        onClick={() => setDeleteConfirm(file.id)}
        aria-label="Delete audio file"
        className="flex-none text-mist-500 transition-colors hover:text-[#F0928C]"
      >
        <Icon name="trash" size={16} />
      </button>
    );

  return (
    <div className="fx max-w-[1120px] flex flex-col gap-[18px]">
      {/* Hidden audio elements */}
      {audioFiles.map(file => (
        <audio
          key={file.id}
          ref={el => {
            audioRefs.current[file.id] = el;
          }}
          src={file.public_url}
          onPlay={() => setPlayingId(file.id)}
          onPause={() => setPlayingId(prev => (prev === file.id ? null : prev))}
          onEnded={() => setPlayingId(prev => (prev === file.id ? null : prev))}
          className="hidden"
        />
      ))}

      {/* Top row: search + caption */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-[360px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mist-500 pointer-events-none">
            <Icon name="search" size={16} />
          </span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search loaded files…"
            className="input !pl-10 !py-[11px] !text-[13.5px] !rounded-[10px] !bg-ink-850"
          />
        </div>
        <span className="ml-auto text-mist-500 text-[13px] whitespace-nowrap">
          {audioFiles.length.toLocaleString()}{hasMore ? '+' : ''} files · {formatFileSize(totalBytes)}
        </span>
      </div>

      {audioFiles.length === 0 ? (
        <div className="rounded-2xl border border-ink-700 bg-ink-850 py-16 text-center">
          <span className="inline-flex text-mist-500">
            <Icon name="library" size={38} />
          </span>
          <h3 className="mt-4 text-white font-semibold text-[15px]">No audio files</h3>
          <p className="mt-1 text-mist-400 text-[13.5px]">Get started by generating your first audio.</p>
          <a href="#generate" className="btn-grad mt-6">
            Generate Audio
          </a>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-ink-700 bg-ink-850 py-12 text-center">
          <p className="text-mist-400 text-[13.5px]">No files match “{search}”.</p>
        </div>
      ) : (
        <>
          {/* Desktop table (lg+) */}
          <div className="hidden lg:block rounded-2xl border border-ink-700 bg-ink-850 overflow-hidden">
            <div
              className={`${GRID_COLS} px-5 py-[13px] border-b border-ink-800 text-mist-500 text-[11.5px] font-semibold uppercase tracking-[0.1em]`}
            >
              <span />
              <span>Title</span>
              <span>Voice</span>
              <span>Length</span>
              <span>Size</span>
              <span>Status</span>
            </div>
            {filtered.map((file, i) => (
              <div
                key={file.id}
                className={`${GRID_COLS} px-5 py-[14px] ${i < filtered.length - 1 ? 'border-b border-ink-800' : ''}`}
              >
                {renderPlayButton(file, 'w-9 h-9')}
                <div className="min-w-0">
                  {renderTitle(file, 'text-[14px]')}
                  <div className="mt-[5px] h-4 overflow-hidden">
                    <Waveform seed={fileTitle(file)} active={playingId === file.id} />
                  </div>
                </div>
                <span className="text-mist-300 text-[13.5px] truncate">{file.voice_name || file.voice_id}</span>
                <span className="text-mist-300 text-[13px] font-mono">{formatDuration(file.duration_seconds)}</span>
                <span className="text-mist-500 text-[13px]">{formatFileSize(file.file_size)}</span>
                <div className="flex items-center justify-between gap-2">
                  <StatusBadge status="completed" />
                  <span className="flex items-center gap-2">
                    {deleteConfirm !== file.id && renderRenameButton(file)}
                    {deleteConfirm !== file.id && (
                      <a
                        href={file.public_url}
                        download
                        aria-label="Download audio file"
                        className="flex-none text-mist-500 transition-colors hover:text-white"
                      >
                        <Icon name="download" size={16} />
                      </a>
                    )}
                    {renderDelete(file)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Card list (below lg) */}
          <div className="flex flex-col gap-3 lg:hidden">
            {filtered.map(file => (
              <div
                key={file.id}
                className="flex items-center gap-3 px-[14px] py-[13px] rounded-[13px] border border-ink-800 bg-ink-900"
              >
                {renderPlayButton(file, 'w-[38px] h-[38px]')}
                <div className="flex-1 min-w-0">
                  {renderTitle(file, 'text-[13.5px]')}
                  <p className="mt-[3px] m-0 text-mist-500 text-[11.5px] truncate">
                    {file.voice_name || file.voice_id} · {formatDuration(file.duration_seconds)}
                  </p>
                </div>
                {renderRenameButton(file)}
                {renderDelete(file)}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {hasMore && (
            <div className="flex flex-col items-center gap-2 py-2">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50"
              >
                {loadingMore && <Spinner size={14} />}
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
              <span className="text-mist-500 text-[12px]">Search only covers files loaded so far.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
